import { cloneDeep } from 'lodash'
import i18n from 'i18n'
import { DashboardWidgetData } from 'features/widget/types'
import ViewOption from 'enums/InsightViewOption'
import MetricCategoryItem from 'enums/MetricCategoryItem'
import MetricSeries, { MetricSeriesStrings } from 'enums/MetricSeries'
import messageUtil from 'utils/messageUtil'
import MeasureType from 'enums/MeasureType'
import AnalysisType from 'enums/AnalysisType'
import { UserPermissionStrings } from 'enums/UserPermission'
import * as measureTypeUtil from 'utils/measureTypeUtil'
import { hasInternalAnalysisPermission } from 'utils/permissionUtil'
import { TimelineSeries } from 'schemas/TimelineSeriesSchema'
import { AnnotatedMetricResult } from 'schemas/AnnotatedMetricResultSchema'
import { TimelineWidget, TimelineGroup } from '../types'

export const getSeriesLabel = (series: TimelineSeries, widgetSetting: TimelineWidget) => {
  if (
    ([ViewOption.STAR_RATINGS, ViewOption.SURVEY_SCORES] as string[]).includes(
      widgetSetting.viewOption!
    ) &&
    MetricCategoryItem.NONE === series.category
  ) {
    switch (series.type) {
      case MetricSeries.OVERALL_STAR_RATINGS:
        return messageUtil.measureType(MeasureType.STAR_RATINGS)
      case MetricSeries.OVERALL_SURVEY_SCORES:
        return messageUtil.measureType(MeasureType.SURVEY_SCORES)
      default:
        break
    }
  }
  let label
  switch (widgetSetting.analysisType) {
    case AnalysisType.SW_TIMELINE_COMPARISON:
    case AnalysisType.AM_TIMELINE_COMPARISON:
      label = series.topicName
      break
    default:
      label = measureTypeUtil.getLabel(
        series.type as MetricSeriesStrings,
        widgetSetting.analysisType
      )
      break
  }

  if (series.isChild && series.category !== MetricCategoryItem.NONE) {
    label = messageUtil.measureType(series.category!)
  }

  if (!series.isSupported) {
    label += i18n.t('notSupportedWithParenthesis')
  }
  return label
}

export const filterResultByPermission = (
  results: DashboardWidgetData<AnnotatedMetricResult>[],
  userPermissions: UserPermissionStrings[]
) => {
  const clonedResult = cloneDeep(results)
  const metricSet = clonedResult[0].results![0].data.metrics
  const hasPermission = hasInternalAnalysisPermission(userPermissions)
  if (!hasPermission && metricSet) {
    metricSet.forEach((metric) => {
      // eslint-disable-next-line no-param-reassign
      metric.dataset = metric.dataset!.filter(
        (data) =>
          !([
            MetricSeries.OVERALL_STAR_RATINGS,
            MetricSeries.OVERALL_SURVEY_SCORES,
            MetricSeries.NET_PROMOTER_SCORES
          ] as string[]).includes(data.seriesName!)
      )
    })
  }

  return clonedResult
}

export const getComparisonModeLangKey = (comparisonMode: string) => {
  const emptyMatrixedItemsErrorMap: Record<string, string> = {
    TAGS: 'tag_other',
    DOC_HIERARCHIES: 'product_other',
    THEMES: 'theme_other'
  }
  return emptyMatrixedItemsErrorMap[comparisonMode]
}

export const canDisplayAnnotation = (widgetSetting: TimelineGroup) =>
  'timelineAnnotation' in widgetSetting && widgetSetting.timelineAnnotation
